// src/pages/JobSearchPage.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  MapPin,
  Building2,
  Wifi,
  Heart,
  ArrowUpRight,
  Plus,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  Euro,
  AlertCircle,
  SlidersHorizontal,
  Sparkles,
  Lock
} from 'lucide-react';
import api from '../api/api';
import CreateJobModal from '../components/CreateJobModal';
import useAuth from '../hooks/useAuth';
import styles from './JobSearchPage.module.css';

// ─────────────────────────────────────────────────────────────
// SKELETON
// ─────────────────────────────────────────────────────────────
const JobSkeleton = () => (
  <div className={styles.skeleton}>
    <div className={styles.skeletonHeader}>
      <div className={styles.skeletonLeft}>
        <div className={`${styles.skeletonLine} ${styles.skeletonTitle}`} />
        <div className={styles.skeletonMeta}>
          <div className={`${styles.skeletonLine} ${styles.skeletonMetaItem}`} />
          <div className={`${styles.skeletonLine} ${styles.skeletonMetaItem}`} />
        </div>
      </div>
      <div className={`${styles.skeletonLine} ${styles.skeletonBtn}`} />
    </div>
    <div className={`${styles.skeletonLine} ${styles.skeletonBody}`} />
    <div className={styles.skeletonTags}>
      {[1, 2, 3].map((i) => (
        <div key={i} className={`${styles.skeletonLine} ${styles.skeletonTag}`} />
      ))}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────
const JobSearchPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.type === 'Admin';

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [location, setLocation] = useState('');
  const [remote, setRemote] = useState('');
  const [showSavedOnly, setShowSavedOnly] = useState(false);

  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchJobs = async (pageToFetch = 1, savedFilter = showSavedOnly) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        q: query,
        l: location,
        remote,
        saved_only: savedFilter,
        page: pageToFetch,
        limit: 15,
      });
      const response = await api.get(`/jobs/search?${params.toString()}`);
      setResults(response.data.results);
      setPagination(response.data.pagination);
      if (response.data.results.length === 0) {
        setError('Aucune offre trouvée pour ces critères.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la recherche.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJobs(1);
  };

  const toggleSavedFilter = () => {
    const newValue = !showSavedOnly;
    setShowSavedOnly(newValue);
    fetchJobs(1, newValue);
  };

  const toggleFavorite = async (jobId, currentIndex) => {
    try {
      const newResults = [...results];
      newResults[currentIndex].is_saved = !newResults[currentIndex].is_saved;
      setResults(newResults);
      await api.post(`/jobs/${jobId}/favorite`);
    } catch (err) {
      console.error('Erreur favoris', err);
      const revertedResults = [...results];
      revertedResults[currentIndex].is_saved =
        !revertedResults[currentIndex].is_saved;
      setResults(revertedResults);
    }
  };

  return (
    <div className={styles.page}>

      {/* ── En-tête ── */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerBadge}>
            <Sparkles size={13} strokeWidth={2.5} />
            Opportunités
          </div>
          <h1 className={styles.pageTitle}>Missions Freelance &amp; Portage</h1>
          <p className={styles.pageSubtitle}>
            {pagination.total_items
              ? `${pagination.total_items} mission${pagination.total_items > 1 ? 's' : ''} disponible${pagination.total_items > 1 ? 's' : ''}`
              : 'Trouvez votre prochaine mission.'}
          </p>
        </div>

        {isAdmin && (
          <button
            className={styles.createBtn}
            onClick={() => setIsModalOpen(true)}
          >
            <Plus size={16} strokeWidth={2.5} />
            Créer une mission
          </button>
        )}
      </div>

      <CreateJobModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => fetchJobs(1)}
      />

      {/* ── Formulaire de recherche ── */}
      <form className={styles.searchForm} onSubmit={handleSearch}>
        <div className={styles.searchFields}>
          {/* Mots-clés */}
          <div className={styles.fieldWrap}>
            <Search
              size={16}
              strokeWidth={2}
              className={styles.fieldIcon}
            />
            <input
              type="text"
              placeholder="Mots-clés (React, Python…)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={styles.field}
            />
          </div>

          {/* Localisation */}
          <div className={styles.fieldWrap}>
            <MapPin
              size={16}
              strokeWidth={2}
              className={styles.fieldIcon}
            />
            <input
              type="text"
              placeholder="Ville ou Code Postal"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className={`${styles.field} ${styles.fieldLocation}`}
            />
          </div>

          {/* Télétravail */}
          <div className={styles.fieldWrap}>
            <Wifi
              size={16}
              strokeWidth={2}
              className={styles.fieldIcon}
            />
            <select
              value={remote}
              onChange={(e) => setRemote(e.target.value)}
              className={`${styles.field} ${styles.fieldSelect}`}
            >
              <option value="">Télétravail (Tous)</option>
              <option value="Oui">Possible</option>
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className={styles.searchActions}>
          <button
            type="button"
            onClick={toggleSavedFilter}
            className={`${styles.filterBtn} ${showSavedOnly ? styles.filterBtnActive : ''}`}
          >
            <Heart
              size={15}
              strokeWidth={2}
              className={showSavedOnly ? styles.heartFilled : ''}
            />
            {showSavedOnly ? 'Mes favoris' : 'Favoris'}
          </button>

          <button type="submit" className={styles.searchBtn}>
            <Search size={16} strokeWidth={2.5} />
            Rechercher
          </button>
        </div>
      </form>

      {/* ── Barre de filtres actifs ── */}
      {(query || location || remote || showSavedOnly) && (
        <div className={styles.activeFilters}>
          <SlidersHorizontal
            size={13}
            strokeWidth={2}
            className={styles.activeFiltersIcon}
          />
          <span className={styles.activeFiltersLabel}>Filtres actifs :</span>
          {query && <span className={styles.filterTag}>{query}</span>}
          {location && <span className={styles.filterTag}>{location}</span>}
          {remote && <span className={styles.filterTag}>Télétravail</span>}
          {showSavedOnly && (
            <span className={`${styles.filterTag} ${styles.filterTagSaved}`}>
              Favoris uniquement
            </span>
          )}
        </div>
      )}

      {/* ── Erreur ── */}
      {error && (
        <div className={styles.errorBox}>
          <AlertCircle size={16} strokeWidth={2} />
          {error}
        </div>
      )}

      {/* ── Liste ── */}
      <div className={styles.jobList}>
        {loading
          ? Array(5)
              .fill(null)
              .map((_, idx) => <JobSkeleton key={idx} />)
          : results.map((job, index) => (
              <JobCard
                key={job.id}
                job={job}
                onToggleFavorite={() => toggleFavorite(job.id, index)}
              />
            ))}
      </div>

      {/* ── Pagination ── */}
      {!loading && pagination.total_pages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            onClick={() => fetchJobs(pagination.current_page - 1)}
            disabled={!pagination.has_prev}
          >
            <ChevronLeft size={16} strokeWidth={2} />
            Précédent
          </button>

          <div className={styles.pageInfo}>
            <span className={styles.pageCurrent}>{pagination.current_page}</span>
            <span className={styles.pageSlash}>/</span>
            <span className={styles.pageTotal}>{pagination.total_pages}</span>
          </div>

          <button
            className={styles.pageBtn}
            onClick={() => fetchJobs(pagination.current_page + 1)}
            disabled={!pagination.has_next}
          >
            Suivante
            <ChevronRight size={16} strokeWidth={2} />
          </button>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// CARTE JOB
// ─────────────────────────────────────────────────────────────
const JobCard = ({ job, onToggleFavorite }) => (
  <article className={styles.jobCard}>
    {/* Bouton favori */}
    <button
      className={`${styles.favoriteBtn} ${job.is_saved ? styles.favoriteBtnActive : ''}`}
      onClick={onToggleFavorite}
      title={job.is_saved ? 'Retirer des favoris' : 'Ajouter aux favoris'}
      aria-label={job.is_saved ? 'Retirer des favoris' : 'Ajouter aux favoris'}
    >
      <Heart
        size={16}
        strokeWidth={2}
        className={job.is_saved ? styles.heartFilled : ''}
      />
    </button>

    {/* En-tête carte */}
    <div className={styles.cardHeader}>
      {/* Icône entreprise */}
      <div className={styles.companyIcon}>
        <Briefcase size={20} strokeWidth={1.75} />
      </div>

      <div className={styles.cardTitleBlock}>
        <h3 className={styles.jobTitle}>{job.title}</h3>
        <div className={styles.jobMeta}>
          {job.is_private && (
            <>
            <span className={styles.metaItem} style={{ color: '#f59e0b', fontWeight: '600' }}>
                <Lock size={13} strokeWidth={2.5} />
                Privée
            </span>
            <span className={styles.metaDivider} />
            </>
          )}
          <span className={styles.metaItem}>
            <Building2 size={13} strokeWidth={2} />
            {job.company}
          </span>
          <span className={styles.metaDivider} />
          <span className={styles.metaItem}>
            <MapPin size={13} strokeWidth={2} />
            {job.location}
          </span>
          {job.salary && job.salary !== 'Non communiqué' && (
            <>
              <span className={styles.metaDivider} />
              <span className={`${styles.metaItem} ${styles.metaSalary}`}>
                <Euro size={13} strokeWidth={2} />
                {job.salary}
              </span>
            </>
          )}
        </div>
      </div>
    </div>

    {/* Résumé */}
    {job.summary && (
      <p className={styles.jobSummary}>{job.summary}</p>
    )}

    {/* Footer carte */}
    <div className={styles.cardFooter}>
      <div className={styles.skillList}>
        {job.skills?.map((skill, i) => (
          <span key={i} className={styles.skillTag}>
            {skill}
          </span>
        ))}
      </div>

      <Link to={`/job-search/${job.id}`} className={styles.detailLink}>
        Voir les détails
        <ArrowUpRight size={14} strokeWidth={2.5} />
      </Link>
    </div>
  </article>
);

export default JobSearchPage;